import React, { useEffect, useState } from "react";
import { api, EXPENSE_CATEGORIES, fmtEuro, todayISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Receipt, Loader2, Paperclip, Image as ImageIcon } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const catMap = Object.fromEntries(EXPENSE_CATEGORIES.map((c) => [c.key, c]));

const EMPTY = { date: todayISO(), category: "carburant", amount: 0, notes: "", receipt_base64: null };

export default function Depenses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expenses?limit=200");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Fichier trop lourd (max 2 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, receipt_base64: reader.result });
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) || 0 });
      toast.success("Dépense enregistrée");
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette dépense ?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Supprimée");
    load();
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);
  const total = filtered.reduce((s, i) => s + (i.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Comptabilité</div>
          <h1 className="font-manrope text-3xl md:text-4xl font-extrabold">Dépenses</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filtered.length} entrées • Total : <span className="text-foreground font-semibold num">{fmtEuro(total)}</span>
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-expense">
              <Plus className="h-4 w-4 mr-2" /> Nouvelle dépense
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-manrope">Ajouter une dépense</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Date</Label>
                  <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="input-expense-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Montant (€)</Label>
                  <Input type="number" step="0.01" required inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="input-expense-amount" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="select-expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Commentaires</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="input-expense-notes" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Justificatif (photo, max 2 Mo)</Label>
                <label className="flex items-center gap-2 border border-dashed border-border rounded-md px-3 py-2 cursor-pointer hover:border-primary/40 transition-colors">
                  <Paperclip className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{form.receipt_base64 ? "Photo attachée ✓" : "Choisir une image"}</span>
                  <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="input-expense-receipt" />
                </label>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="btn-cancel-expense">Annuler</Button>
                <Button type="submit" disabled={saving} data-testid="btn-save-expense">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        <ChipBtn active={filter === "all"} onClick={() => setFilter("all")} testid="chip-all">Tout</ChipBtn>
        {EXPENSE_CATEGORIES.map((c) => (
          <ChipBtn key={c.key} active={filter === c.key} onClick={() => setFilter(c.key)} testid={`chip-${c.key}`}>
            {c.label}
          </ChipBtn>
        ))}
      </div>

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="border-dashed border-2 border-border/50 m-4 rounded-xl p-10 text-center">
            <Receipt className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <div className="font-manrope font-semibold text-lg mb-1">Aucune dépense</div>
            <div className="text-sm text-muted-foreground">Ajoutez vos frais professionnels pour un suivi précis.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Catégorie</th>
                  <th className="text-left px-4 py-3">Commentaires</th>
                  <th className="text-right px-4 py-3">Montant</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody data-testid="expenses-tbody">
                {filtered.map((e) => {
                  const cat = catMap[e.category];
                  const Icon = cat && LucideIcons[cat.icon] ? LucideIcons[cat.icon] : ImageIcon;
                  return (
                    <tr key={e.id} className="border-b border-border/60 hover:bg-muted/40 transition-colors">
                      <td className="px-4 py-3 num">{e.date}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center">
                            <Icon className="h-3.5 w-3.5 text-primary" />
                          </div>
                          <span>{cat?.label || e.category}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs truncate max-w-[240px]">{e.notes || "—"}</td>
                      <td className="px-4 py-3 text-right num font-semibold">{fmtEuro(e.amount)}</td>
                      <td className="px-2 py-3 text-right">
                        <button
                          onClick={() => remove(e.id)}
                          className="text-muted-foreground hover:text-destructive p-1.5 rounded-md hover:bg-destructive/10 transition-colors"
                          data-testid={`btn-delete-expense-${e.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ChipBtn({ children, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap border transition-colors ${
        active
          ? "bg-primary/15 text-primary border-primary/40"
          : "bg-card text-muted-foreground border-border hover:border-primary/30"
      }`}
    >
      {children}
    </button>
  );
}
